import { useFormik } from 'formik';
import { FormTemplate } from '@employ-engage/data';
import * as Yup from 'yup';
import ErrorMessage from '../error-message/error-message';
import { useTranslation } from 'react-i18next';
import { DUPLICATE_FORM_TEMPLATE } from '../api';
import { useMutation } from '@apollo/client';

export interface DuplicateTemplateFormProps<T> {
  onClose: () => void;
  templateSelectedForDuplication: T;
  refetchFormTemplates: () => void;
}

export function DuplicateTemplateForm<
  T extends FormTemplate
>({
  onClose,
  templateSelectedForDuplication,
  refetchFormTemplates,
}: DuplicateTemplateFormProps<T>) {
  const { t } = useTranslation(['common']);
  const [duplicateFormTemplate] = useMutation(DUPLICATE_FORM_TEMPLATE);

  const { handleSubmit, values, errors, touched, handleChange } = useFormik({
    initialValues: {
      name: templateSelectedForDuplication?.name + ' ' + t('(copy)'),
      template: templateSelectedForDuplication as FormTemplate,
    },
    onSubmit: async (values, { resetForm }) => {
      if (values.template) {
        try {
          await duplicateFormTemplate({
            variables: {
              input: {
                templateId: values.template.id,
                newName: values.name,
              },
            },
          });
          refetchFormTemplates();
          resetForm();
          onClose();
        } catch (error) {
          console.error('Error duplicating template:', error);
        }
      } else {
        throw new Error('Template is required');
      }
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required(t('Required')),
    }),
  });
  return (
    <form className="mt-10" onSubmit={handleSubmit}>
      <label
        htmlFor="name"
        className="block text-sm font-medium leading-5 text-gray-700"
      >
        {t('New Template Name')}
      </label>
      <div className="mt-1 rounded-md shadow-sm">
        <input
          id="name"
          name="name"
          type="text"
          value={values.name}
          onChange={handleChange}
          className="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
        />
        <ErrorMessage errors={errors.name} touched={touched.name} />
      </div>
      <div className="mt-6">
        <button
          type="submit"
          className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-light-primary hover:bg-light-primaryAccent focus:outline-none focus:border-light-primaryAccent focus:shadow-outline-indigo active:bg-light-primaryAccent transition duration-150 ease-in-out"
        >
          {t('Duplicate Template')}
        </button>
      </div>
    </form>
  );
}

export default DuplicateTemplateForm;
